// 角色列表（/api/instances）。
// 后端按角色键合并在线内存视图与 DB 最后会话快照；instance_id 仍作为当前连接操作目标。

import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Play, RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { apiGet, apiPostForm } from "@/api/client";
import {
  API_INSTANCES,
  instanceDetailPath,
  instanceDeletePath,
  instanceRestartGamePath,
  roleLaunchBindingStartPath,
} from "@/api/endpoints";
import type {
  InstanceDeleteResponse,
  InstanceDetailResponse,
  InstanceListFilterOption,
  InstanceListFilterOptions,
  InstanceListResponse,
  RoleLaunchBindingActionResponse,
} from "@/api/types";
import { QueryView } from "@/components/ui/QueryView";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { Button } from "@/components/ui/Button";
import { Checkbox } from "@/components/ui/Checkbox";
import { Input } from "@/components/ui/Input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/Table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import { ConfirmDialog } from "@/components/forms/ConfirmDialog";
import { useSession } from "@/hooks/useSession";
import { useSSESubscription, type SSEMessage } from "@/hooks/useSSESubscription";
import {
  compactNumber,
  formatCombatPower,
  formatCombatPowerRank,
} from "@/lib/format";

export const Route = createFileRoute("/instances/")({
  validateSearch: parseInstancesSearch,
  component: InstancesIndexRoute,
});

const PROFIT_CURRENCY_ORDER = [
  { id: "2", name: "元宝" },
  { id: "14", name: "积分" },
  { id: "7", name: "灵符" },
  { id: "1", name: "金币" },
];
const EMPTY_INSTANCE_ROWS: InstanceListResponse["items"] = [];
const UNKNOWN_RESTART_AGENT_KEY = "agent:unknown";
const INSTANCE_LIST_FALLBACK_REFETCH_MS = 120_000;
const INSTANCE_LIST_SSE_REFETCH_THROTTLE_MS = 5_000;
const INSTANCE_PAGE_SIZE = 10;
const ALL_FILTER_VALUE = "all";
const EMPTY_INSTANCES_SEARCH: InstancesSearch = {};
const noopInstancesSearchChange = () => undefined;

type RestartBatchMode = "selected" | "all";
type StartBatchMode = "selected" | "all";

export interface InstancesSearch {
  server?: string;
  game?: string;
  account?: string;
  agent?: string;
  page?: number;
}

interface RestartTarget {
  instanceId: string;
  label: string;
  game?: string;
  server?: string;
  agentKey: string;
  agentLabel: string;
}

interface StartCandidate {
  instanceId: string;
  label: string;
  game?: string;
  server?: string;
  agentKey: string;
  agentLabel: string;
}

interface StartTarget extends StartCandidate {
  bindingId: number;
}

interface RestartBatchResult {
  instanceId: string;
  label: string;
  ok: boolean;
  error?: string;
}

interface RestartBatchState {
  running: boolean;
  mode: RestartBatchMode;
  total: number;
  completed: number;
  ok: number;
  failed: number;
  intervalSec: number;
  agentCount: number;
  currentByAgent: Record<string, string>;
  waitingByAgent: Record<string, string>;
  results: RestartBatchResult[];
}

interface StartBatchState {
  running: boolean;
  mode: StartBatchMode;
  total: number;
  completed: number;
  ok: number;
  failed: number;
  intervalSec: number;
  agentCount: number;
  resolving: boolean;
  currentByAgent: Record<string, string>;
  waitingByAgent: Record<string, string>;
  results: RestartBatchResult[];
}

interface RestartTargetGroup {
  key: string;
  label: string;
  targets: RestartTarget[];
}

interface StartTargetGroup {
  key: string;
  label: string;
  targets: StartTarget[];
}

function InstancesIndexRoute() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: "/instances/" });
  return (
    <InstancesView
      search={search}
      onChange={(next) => navigate({ search: compactInstancesSearch(next) })}
    />
  );
}

function parseInstancesSearch(search: Record<string, unknown>): InstancesSearch {
  const pick = (value: unknown): string | undefined => {
    if (typeof value !== "string") return undefined;
    const text = value.trim();
    return text === "" || text === ALL_FILTER_VALUE ? undefined : text;
  };
  let page: number | undefined;
  if (search.page !== undefined && search.page !== null && search.page !== "") {
    const parsed = Number(search.page);
    if (Number.isFinite(parsed) && parsed > 1) {
      page = Math.trunc(parsed);
    }
  }
  return {
    server: pick(search.server),
    game: pick(search.game),
    account: pick(search.account),
    agent: pick(search.agent),
    page,
  };
}

function compactInstancesSearch(search: InstancesSearch): InstancesSearch {
  return {
    ...(search.server ? { server: search.server } : {}),
    ...(search.game ? { game: search.game } : {}),
    ...(search.account ? { account: search.account } : {}),
    ...(search.agent ? { agent: search.agent } : {}),
    ...(search.page && search.page > 1 ? { page: search.page } : {}),
  };
}

function buildInstancesQuery(search: InstancesSearch) {
  const params = new URLSearchParams();
  params.set("limit", String(INSTANCE_PAGE_SIZE));
  params.set("page", String(pageFromSearch(search)));
  if (search.server) params.set("server", search.server);
  if (search.game) params.set("game", search.game);
  if (search.account) params.set("account", search.account);
  if (search.agent) params.set("agent", search.agent);
  return params.toString();
}

function instancesQueryKey(search: InstancesSearch) {
  return ["instances", compactInstancesSearch(search)] as const;
}

function instanceListPath(search: InstancesSearch) {
  return `${API_INSTANCES}?${buildInstancesQuery(search)}`;
}

function restartAgentKey(row: InstanceListResponse["items"][number]) {
  if (row.agent_id) return `agent:${row.agent_id}`;
  if (row.agent_hostname && row.agent_mac_id) {
    return `host:${row.agent_hostname}:${row.agent_mac_id}`;
  }
  if (row.agent_hostname) return `host:${row.agent_hostname}`;
  const peerHost = peerHostFromAddress(row.peer);
  if (peerHost) return `peer:${peerHost}`;
  return UNKNOWN_RESTART_AGENT_KEY;
}

function restartAgentLabel(row: InstanceListResponse["items"][number]) {
  if (row.agent_hostname) return row.agent_hostname;
  if (row.agent_id) return row.agent_id;
  const peerHost = peerHostFromAddress(row.peer);
  if (peerHost) return peerHost;
  return "未知 agent";
}

function peerHostFromAddress(peer?: string) {
  const text = (peer ?? "").trim();
  if (!text) return "";
  const ipv6Match = text.match(/^\[([^\]]+)\](?::\d+)?$/);
  if (ipv6Match) return ipv6Match[1];
  const lastColon = text.lastIndexOf(":");
  if (lastColon > 0 && /^\d+$/.test(text.slice(lastColon + 1))) {
    return text.slice(0, lastColon);
  }
  return text;
}

function pageFromSearch(search: InstancesSearch) {
  return Math.max(1, search.page ?? 1);
}

function pageRange(total: number, page: number) {
  if (total === 0) return { start: 0, end: 0 };
  return {
    start: (page - 1) * INSTANCE_PAGE_SIZE + 1,
    end: Math.min(page * INSTANCE_PAGE_SIZE, total),
  };
}

function restartTargetGroups(targets: RestartTarget[]): RestartTargetGroup[] {
  const byKey = new Map<string, RestartTargetGroup>();
  for (const target of targets) {
    const key = target.agentKey || UNKNOWN_RESTART_AGENT_KEY;
    const current =
      byKey.get(key) ??
      ({
        key,
        label: target.agentLabel || "未知 agent",
        targets: [],
      } satisfies RestartTargetGroup);
    current.targets.push(target);
    byKey.set(key, current);
  }
  return [...byKey.values()];
}

function startTargetGroups(targets: StartTarget[]): StartTargetGroup[] {
  const byKey = new Map<string, StartTargetGroup>();
  for (const target of targets) {
    const key = target.agentKey || UNKNOWN_RESTART_AGENT_KEY;
    const current =
      byKey.get(key) ??
      ({
        key,
        label: target.agentLabel || "未知 agent",
        targets: [],
      } satisfies StartTargetGroup);
    current.targets.push(target);
    byKey.set(key, current);
  }
  return [...byKey.values()];
}

function labelMapWith(
  current: Record<string, string>,
  key: string,
  value: string,
) {
  return { ...current, [key]: value };
}

function labelMapWithout(current: Record<string, string>, key: string) {
  const next = { ...current };
  delete next[key];
  return next;
}

type InstanceListItem = InstanceListResponse["items"][number];
type ProfitSummary = NonNullable<NonNullable<InstanceListItem["profit"]>>;
type ProfitCurrencyAmount = ProfitSummary["currencies"][number];
type ProfitMaterialAmount = ProfitSummary["materials"][number];
type ProfitAnomaly = NonNullable<ProfitSummary["anomalies"]>[number];

function mergeInstanceListUpdate(
  current: InstanceListResponse | undefined,
  payload: SSEMessage,
): InstanceListResponse | undefined {
  if (!current) return current;
  const nextItem = instanceListItemFromUpdate(payload);
  if (!nextItem) return undefined;
  let changed = false;
  const items = current.items.map((item) => {
    if (!instanceListItemMatchesUpdate(item, nextItem)) return item;
    changed = true;
    return mergeInstanceListItem(item, nextItem);
  });
  if (!changed) return undefined;
  return { ...current, items };
}

function mergeInstanceProfitUpdate(
  current: InstanceListResponse | undefined,
  payload: SSEMessage,
): InstanceListResponse | undefined {
  if (!current) return current;
  const instanceID = stringFromEvent(payload.instance_id);
  const profit = profitSummaryFromEvent(payload.profit);
  if (!instanceID || !profit) return current;
  let changed = false;
  const items = current.items.map((item) => {
    if (!instanceListItemMatchesInstanceID(item, instanceID)) return item;
    changed = true;
    return { ...item, profit };
  });
  if (!changed) return current;
  return { ...current, items };
}

function instanceListItemFromUpdate(payload: SSEMessage): Partial<InstanceListItem> | null {
  const summary = eventRecord(payload.summary);
  const instance = eventRecord(payload.instance);
  const source = { ...instance, ...summary };
  const instanceID =
    stringFromEvent(payload.instance_id) || stringFromEvent(source.instance_id);
  if (!instanceID) return null;
  const gameID =
    stringFromEvent(payload.game_id) || stringFromEvent(source.game_id);
  const serverID =
    stringFromEvent(payload.server_id) || stringFromEvent(source.server_id);
  const roleID =
    stringFromEvent(payload.role_id) || stringFromEvent(source.role_id);
  const next: Partial<InstanceListItem> = {
    instance_id: instanceID,
    role_id: roleID || undefined,
    role_key: roleKeyFromParts(gameID, serverID, roleID) || undefined,
    online: true,
    state: stringFromEvent(source.state) || undefined,
    game_account:
      stringFromEvent(payload.game_account) ||
      stringFromEvent(source.game_account) ||
      undefined,
    role_name:
      stringFromEvent(payload.role_name) ||
      stringFromEvent(source.role_name) ||
      undefined,
    server_id: serverID || undefined,
    server_name:
      stringFromEvent(payload.server_name) ||
      stringFromEvent(source.server_name) ||
      undefined,
    game_id: gameID || undefined,
    game_name: stringFromEvent(source.game_name) || undefined,
    peer: stringFromEvent(source.peer) || undefined,
    resource_root: stringFromEvent(source.resource_root) || undefined,
    cache_root: stringFromEvent(source.cache_root) || undefined,
    cache_id: stringFromEvent(source.cache_id) || undefined,
    last_seen_at: msFromEvent(payload.game_state_at ?? source.game_state_at),
    current_map_id: stringFromEvent(source.current_map_id) || undefined,
    current_map_name: stringFromEvent(source.current_map_name) || undefined,
    currencies: metricValuesFromEvent(source.currencies),
    materials: metricValuesFromEvent(source.materials),
  };
  setNumberField(next, "account_id", source.account_id);
  setNumberField(next, "combat_power", source.combat_power);
  setNumberField(next, "combat_power_rank", source.combat_power_rank);
  setNumberField(next, "evolution_level", source.evolution_level);
  setNumberField(next, "title_count", source.title_count);
  setStringField(next, "evolution_text", source.evolution_text);
  setStringField(next, "vip_level", source.vip_level);
  setStringField(next, "vip_label", source.vip_label);
  setStringField(next, "active_title_name", source.active_title_name);
  return next;
}

function mergeInstanceListItem(
  current: InstanceListItem,
  update: Partial<InstanceListItem>,
): InstanceListItem {
  return {
    ...current,
    ...definedEntries(update),
    online: update.online ?? current.online,
    state: update.state ?? current.state,
    profit: current.profit,
    merged_count: current.merged_count,
    merged_instance_ids: current.merged_instance_ids,
  };
}

function instanceListItemMatchesUpdate(
  item: InstanceListItem,
  update: Partial<InstanceListItem>,
) {
  if (!update.instance_id) return false;
  if (instanceListItemMatchesInstanceID(item, update.instance_id)) return true;
  return Boolean(update.role_key && item.role_key === update.role_key);
}

function instanceListItemMatchesInstanceID(item: InstanceListItem, instanceID: string) {
  return (
    item.instance_id === instanceID ||
    (item.merged_instance_ids ?? []).includes(instanceID)
  );
}

function eventRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function stringFromEvent(value: unknown): string {
  if (typeof value === "string") return value.trim();
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return "";
}

function roleKeyFromParts(gameID: string, serverID: string, roleID: string) {
  if (!gameID || !serverID || !roleID) return "";
  return `${gameID}:${serverID}:${roleID}`;
}

function msFromEvent(value: unknown): number | undefined {
  if (typeof value !== "number" || !Number.isFinite(value)) return undefined;
  return value > 10_000_000_000 ? Math.round(value) : Math.round(value * 1000);
}

function setNumberField<T extends keyof InstanceListItem>(
  target: Partial<InstanceListItem>,
  key: T,
  value: unknown,
) {
  if (typeof value === "number" && Number.isFinite(value)) {
    target[key] = value as InstanceListItem[T];
  }
}

function setStringField<T extends keyof InstanceListItem>(
  target: Partial<InstanceListItem>,
  key: T,
  value: unknown,
) {
  const text = stringFromEvent(value);
  if (text) target[key] = text as InstanceListItem[T];
}

function metricValuesFromEvent(value: unknown): InstanceListItem["currencies"] {
  if (!Array.isArray(value)) return undefined;
  const out: NonNullable<InstanceListItem["currencies"]> = [];
  for (const entry of value) {
    const record = eventRecord(entry);
    const count = numberFromEvent(record.count);
    if (count === undefined) continue;
    out.push({
      id: stringFromEvent(record.id) || undefined,
      label: stringFromEvent(record.label) || undefined,
      count,
    });
  }
  return out;
}

function profitSummaryFromEvent(value: unknown): ProfitSummary | null {
  const record = eventRecord(value);
  if (Object.keys(record).length === 0) return null;
  return {
    window_minutes: numberFromEvent(record.window_minutes) ?? 0,
    active_seconds: numberFromEvent(record.active_seconds) ?? 0,
    wall_seconds: numberFromEvent(record.wall_seconds),
    source: stringFromEvent(record.source) || undefined,
    sample_status: stringFromEvent(record.sample_status) || "unknown",
    currencies: profitCurrenciesFromEvent(record.currencies),
    materials: profitMaterialsFromEvent(record.materials),
    value_currency: stringFromEvent(record.value_currency) || undefined,
    gross_value: numberFromEvent(record.gross_value),
    cost_value: numberFromEvent(record.cost_value),
    net_value: numberFromEvent(record.net_value),
    net_value_per_hour: numberFromEvent(record.net_value_per_hour),
    anomalies: profitAnomaliesFromEvent(record.anomalies),
    updated_at: numberFromEvent(record.updated_at),
  };
}

function profitCurrenciesFromEvent(value: unknown): ProfitCurrencyAmount[] {
  if (!Array.isArray(value)) return [];
  const out: ProfitCurrencyAmount[] = [];
  for (const entry of value) {
    const record = eventRecord(entry);
    out.push({
      id: stringFromEvent(record.id),
      name: stringFromEvent(record.name) || undefined,
      gain: numberFromEvent(record.gain) ?? 0,
      spend: numberFromEvent(record.spend) ?? 0,
      net: numberFromEvent(record.net) ?? 0,
      gain_per_hour: numberFromEvent(record.gain_per_hour),
      spend_per_hour: numberFromEvent(record.spend_per_hour),
      net_per_hour: numberFromEvent(record.net_per_hour),
      value: numberFromEvent(record.value),
      value_per_hour: numberFromEvent(record.value_per_hour),
    });
  }
  return out;
}

function profitMaterialsFromEvent(value: unknown): ProfitMaterialAmount[] {
  if (!Array.isArray(value)) return [];
  const out: ProfitMaterialAmount[] = [];
  for (const entry of value) {
    const record = eventRecord(entry);
    const itemID = numberFromEvent(record.item_id);
    const key =
      stringFromEvent(record.key) ||
      (itemID === undefined
        ? `name:${stringFromEvent(record.name) || "unknown"}`
        : String(Math.trunc(itemID)));
    out.push({
      key,
      item_id: itemID === undefined ? undefined : Math.trunc(itemID),
      name: stringFromEvent(record.name) || undefined,
      gain: numberFromEvent(record.gain) ?? 0,
      gain_per_hour: numberFromEvent(record.gain_per_hour),
      value: numberFromEvent(record.value),
      value_per_hour: numberFromEvent(record.value_per_hour),
    });
  }
  return out;
}

function profitAnomaliesFromEvent(value: unknown): ProfitAnomaly[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const out: ProfitAnomaly[] = [];
  for (const entry of value) {
    const record = eventRecord(entry);
    const type = stringFromEvent(record.type);
    const severity = stringFromEvent(record.severity);
    const message = stringFromEvent(record.message);
    if (!type && !severity && !message) continue;
    out.push({
      type,
      severity,
      message,
      map_id: stringFromEvent(record.map_id) || undefined,
      map_name: stringFromEvent(record.map_name) || undefined,
      current_value: numberFromEvent(record.current_value),
      baseline_value: numberFromEvent(record.baseline_value),
    });
  }
  return out.length > 0 ? out : undefined;
}

function numberFromEvent(value: unknown): number | undefined {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  return undefined;
}

function positiveNumberFromUnknown(value: unknown): number {
  if (typeof value === "number" && Number.isFinite(value) && value > 0) {
    return Math.trunc(value);
  }
  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value);
    if (Number.isFinite(parsed) && parsed > 0) {
      return Math.trunc(parsed);
    }
  }
  return 0;
}

function launchContextBlockerText(
  launchContext: NonNullable<InstanceDetailResponse["launch_context"]>,
): string {
  const blockers = launchContext.blockers ?? [];
  if (blockers.length === 0) return "启动条件未满足";
  return blockers
    .map((blocker) => blocker.message || blocker.code)
    .filter(Boolean)
    .join("；") || "启动条件未满足";
}

function definedEntries<T extends object>(value: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(value).filter(([, entry]) => entry !== undefined),
  ) as Partial<T>;
}

interface InstancesViewProps {
  search?: InstancesSearch;
  onChange?: (next: InstancesSearch) => void;
}

export function InstancesView({
  search = EMPTY_INSTANCES_SEARCH,
  onChange = noopInstancesSearchChange,
}: InstancesViewProps = {}) {
  const queryClient = useQueryClient();
  const session = useSession();
  const canWrite = session.data?.can_write ?? false;
  const actor = session.data?.actor ?? "anonymous";
  const [selectedIDs, setSelectedIDs] = useState<Set<string>>(() => new Set());
  const [restartIntervalText, setRestartIntervalText] = useState("10");
  const [confirmMode, setConfirmMode] = useState<RestartBatchMode | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [restartRun, setRestartRun] = useState<RestartBatchState | null>(null);
  const pendingRefreshTimerRef = useRef<number | null>(null);
  const lastRefreshAtRef = useRef(0);
  const queryKey = useMemo(() => instancesQueryKey(search), [search]);

  const q = useQuery({
    queryKey,
    queryFn: () => apiGet<InstanceListResponse>(instanceListPath(search)),
    refetchInterval: INSTANCE_LIST_FALLBACK_REFETCH_MS,
  });
  const rows = q.data?.items ?? EMPTY_INSTANCE_ROWS;
  const filterOptions = q.data?.filter_options ?? {
    servers: [],
    games: [],
    accounts: [],
    agents: [],
  };
  const totalCount = q.data?.total ?? rows.length;
  const onlineCount = q.data?.online_count ?? rows.filter((row) => row.online).length;
  const pageCount = q.data?.page_count ?? 1;
  const currentPage = q.data?.page ?? pageFromSearch(search);
  const pageBounds = pageRange(totalCount, currentPage);
  const onlineRows = useMemo(
    () => rows.filter((row) => row.online),
    [rows],
  );
  const offlineRows = useMemo(
    () => rows.filter((row) => !row.online),
    [rows],
  );
  const selectedRows = useMemo(
    () => rows.filter((row) => selectedIDs.has(row.instance_id)),
    [rows, selectedIDs],
  );
  const selectedOnlineRows = useMemo(
    () => selectedRows.filter((row) => row.online),
    [selectedRows],
  );
  const selectedOfflineRows = useMemo(
    () => selectedRows.filter((row) => !row.online),
    [selectedRows],
  );
  const restartRunning = restartRun?.running ?? false;
  const [startConfirmMode, setStartConfirmMode] = useState<StartBatchMode | null>(null);
  const [startConfirmOpen, setStartConfirmOpen] = useState(false);
  const [startRun, setStartRun] = useState<StartBatchState | null>(null);
  const startRunning = startRun?.running ?? false;
  const batchRunning = restartRunning || startRunning;
  const selectedOnlineCount = selectedOnlineRows.length;
  const selectedOfflineCount = selectedOfflineRows.length;
  const allRowsSelected =
    rows.length > 0 &&
    rows.every((row) => selectedIDs.has(row.instance_id));
  const someRowsSelected =
    !allRowsSelected &&
    rows.some((row) => selectedIDs.has(row.instance_id));

  useEffect(() => {
    const visibleIDs = new Set(rows.map((row) => row.instance_id));
    setSelectedIDs((current) => {
      let changed = false;
      const next = new Set<string>();
      for (const id of current) {
        if (visibleIDs.has(id)) {
          next.add(id);
        } else {
          changed = true;
        }
      }
      return changed ? next : current;
    });
  }, [rows]);

  useEffect(() => {
    if (!q.data) return;
    const requestedPage = pageFromSearch(search);
    if (requestedPage > pageCount) {
      onChange({ ...search, page: pageCount > 1 ? pageCount : undefined });
    }
  }, [onChange, pageCount, q.data, search]);

  const deleteMut = useMutation({
    mutationFn: (instanceId: string) =>
      apiPostForm<InstanceDeleteResponse>(
        instanceDeletePath(instanceId),
        {},
      ),
    onSuccess: () => {
      toast.success("已删除离线角色快照");
      void queryClient.invalidateQueries({ queryKey: ["instances"] });
    },
    onError: (err) => toast.error(`删除失败：${(err as Error).message}`),
  });

  const scheduleInstancesRefresh = useCallback(() => {
    const now = Date.now();
    const elapsed = now - lastRefreshAtRef.current;
    const run = () => {
      pendingRefreshTimerRef.current = null;
      lastRefreshAtRef.current = Date.now();
      void queryClient.invalidateQueries({ queryKey });
    };
    if (elapsed >= INSTANCE_LIST_SSE_REFETCH_THROTTLE_MS) {
      if (pendingRefreshTimerRef.current !== null) {
        window.clearTimeout(pendingRefreshTimerRef.current);
        pendingRefreshTimerRef.current = null;
      }
      run();
      return;
    }
    if (pendingRefreshTimerRef.current !== null) return;
    pendingRefreshTimerRef.current = window.setTimeout(
      run,
      INSTANCE_LIST_SSE_REFETCH_THROTTLE_MS - elapsed,
    );
  }, [queryClient, queryKey]);

  useEffect(() => {
    return () => {
      if (pendingRefreshTimerRef.current !== null) {
        window.clearTimeout(pendingRefreshTimerRef.current);
      }
    };
  }, []);

  useSSESubscription("instance.update", (payload) => {
    if (payload.reason === "lua_disconnected") {
      scheduleInstancesRefresh();
      return;
    }
    const applied = queryClient.setQueryData<InstanceListResponse>(
      queryKey,
      (current) => mergeInstanceListUpdate(current, payload),
    );
    if (!applied) {
      scheduleInstancesRefresh();
    }
  });
  useSSESubscription("instance.deleted", (payload) => {
    void payload;
    scheduleInstancesRefresh();
  });
  useSSESubscription("instance.profit_updated", (payload) => {
    queryClient.setQueryData<InstanceListResponse>(
      queryKey,
      (current) => mergeInstanceProfitUpdate(current, payload),
    );
  });

  function restartIntervalSec() {
    const parsed = Number.parseInt(restartIntervalText, 10);
    if (!Number.isFinite(parsed)) return 10;
    return Math.max(1, Math.min(600, parsed));
  }

  function restartTargets(mode: RestartBatchMode): RestartTarget[] {
    const source = mode === "all" ? onlineRows : selectedOnlineRows;
    return source.map((row) => ({
      instanceId: row.instance_id,
      label: row.role_name || row.instance_id,
      game: row.game_name || row.game_id,
      server: row.server_name || row.server_id,
      agentKey: restartAgentKey(row),
      agentLabel: restartAgentLabel(row),
    }));
  }

  function startCandidates(mode: StartBatchMode): StartCandidate[] {
    const source = mode === "all" ? offlineRows : selectedOfflineRows;
    return source.map((row) => ({
      instanceId: row.instance_id,
      label: row.role_name || row.instance_id,
      game: row.game_name || row.game_id,
      server: row.server_name || row.server_id,
      agentKey: restartAgentKey(row),
      agentLabel: restartAgentLabel(row),
    }));
  }

  function clearPageSelection() {
    const visibleIDs = new Set(rows.map((row) => row.instance_id));
    setSelectedIDs((current) => {
      const next = new Set(current);
      for (const id of visibleIDs) next.delete(id);
      return next;
    });
  }

  function setAllRowsSelected(checked: boolean) {
    if (!checked) {
      clearPageSelection();
      return;
    }
    setSelectedIDs((current) => {
      const next = new Set(current);
      for (const row of rows) next.add(row.instance_id);
      return next;
    });
  }

  function setFilter<K extends keyof Pick<InstancesSearch, "server" | "game" | "account" | "agent">>(
    key: K,
    value: string,
  ) {
    onChange({
      ...search,
      [key]: value === ALL_FILTER_VALUE ? undefined : value,
      page: undefined,
    });
  }

  function resetFilters() {
    setSelectedIDs(new Set());
    onChange({});
  }

  function goToPage(page: number) {
    onChange({ ...search, page: page > 1 ? page : undefined });
  }

  function setRowSelected(instanceID: string, checked: boolean) {
    setSelectedIDs((current) => {
      const next = new Set(current);
      if (checked) {
        next.add(instanceID);
      } else {
        next.delete(instanceID);
      }
      return next;
    });
  }

  function openRestartConfirm(mode: RestartBatchMode) {
    const targets = restartTargets(mode);
    if (targets.length === 0) {
      toast.error(mode === "all" ? "当前页没有在线角色可重启" : "请先选择在线角色");
      return;
    }
    setConfirmMode(mode);
    setConfirmOpen(true);
  }

  function openStartConfirm(mode: StartBatchMode) {
    const targets = startCandidates(mode);
    if (targets.length === 0) {
      toast.error(mode === "all" ? "当前页没有离线角色可启动" : "请先选择离线角色");
      return;
    }
    setStartConfirmMode(mode);
    setStartConfirmOpen(true);
  }

  async function runRestartBatch(mode: RestartBatchMode) {
    const targets = restartTargets(mode);
    const intervalSec = restartIntervalSec();
    const groups = restartTargetGroups(targets);
    if (targets.length === 0) {
      const error = new Error(mode === "all" ? "当前页没有在线角色可重启" : "请先选择在线角色");
      toast.error(error.message);
      throw error;
    }

    const initialState: RestartBatchState = {
      running: true,
      mode,
      total: targets.length,
      completed: 0,
      ok: 0,
      failed: 0,
      intervalSec,
      agentCount: groups.length,
      currentByAgent: {},
      waitingByAgent: {},
      results: [],
    };
    setStartRun(null);
    setRestartRun(initialState);

    let ok = 0;
    let failed = 0;
    let completed = 0;
    const results: RestartBatchResult[] = [];
    await Promise.all(
      groups.map(async (group) => {
        for (let index = 0; index < group.targets.length; index += 1) {
          const target = group.targets[index];
          const activeLabel = `${target.agentLabel} / ${target.label}`;
          setRestartRun((current) =>
            current
              ? {
                  ...current,
                  currentByAgent: labelMapWith(current.currentByAgent, group.key, activeLabel),
                  waitingByAgent: labelMapWithout(current.waitingByAgent, group.key),
                }
              : current,
          );
          try {
            await apiPostForm<{ ok: boolean; error?: string }>(
              instanceRestartGamePath(target.instanceId),
              { reason: `webui:${actor}` },
            );
            ok += 1;
            results.push({
              instanceId: target.instanceId,
              label: target.label,
              ok: true,
            });
          } catch (err) {
            failed += 1;
            results.push({
              instanceId: target.instanceId,
              label: target.label,
              ok: false,
              error: err instanceof Error ? err.message : String(err),
            });
          }

          completed += 1;
          setRestartRun((current) =>
            current
              ? {
                  ...current,
                  completed,
                  ok,
                  failed,
                  currentByAgent: labelMapWithout(current.currentByAgent, group.key),
                  results: [...results],
                }
              : current,
          );
          void queryClient.invalidateQueries({ queryKey });

          if (index < group.targets.length - 1) {
            const nextTarget = group.targets[index + 1];
            setRestartRun((current) =>
              current
                ? {
                    ...current,
                    waitingByAgent: labelMapWith(
                      current.waitingByAgent,
                      group.key,
                      `${nextTarget.agentLabel} / ${nextTarget.label}`,
                    ),
                  }
                : current,
            );
            await delay(intervalSec * 1000);
          }
        }
      }),
    );

    setRestartRun((current) =>
      current
        ? {
            ...current,
            running: false,
            completed: targets.length,
            ok,
            failed,
            currentByAgent: {},
            waitingByAgent: {},
            results: [...results],
          }
        : current,
    );
    if (failed > 0) {
      toast.error(`批量重启完成：成功 ${ok}，失败 ${failed}`);
    } else {
      toast.success(`批量重启已派发：${ok} 个角色`);
    }
    void queryClient.invalidateQueries({ queryKey });
  }

  async function resolveStartTarget(candidate: StartCandidate): Promise<StartTarget> {
    const detail = await apiGet<InstanceDetailResponse>(
      instanceDetailPath(candidate.instanceId),
    );
    if (detail.online) {
      throw new Error("角色已在线，不能按离线槽位启动");
    }
    const launchContext = detail.launch_context;
    if (!launchContext) {
      throw new Error("缺少固定槽位启动上下文");
    }
    if (!launchContext.can_start) {
      throw new Error(launchContextBlockerText(launchContext));
    }
    const bindingID = positiveNumberFromUnknown(launchContext.binding?.id);
    if (!bindingID) {
      throw new Error("启动上下文缺少 role_launch_binding id");
    }
    const slot = launchContext.slot ?? {};
    const agentID =
      stringFromEvent(launchContext.agent_id) || stringFromEvent(slot.agent_id);
    const agentLabel =
      stringFromEvent(slot.hostname) || candidate.agentLabel || agentID || "未知 agent";
    return {
      ...candidate,
      bindingId: bindingID,
      agentKey: agentID ? `agent:${agentID}` : candidate.agentKey,
      agentLabel,
    };
  }

  async function runStartBatch(mode: StartBatchMode) {
    const candidates = startCandidates(mode);
    const intervalSec = restartIntervalSec();
    if (candidates.length === 0) {
      const error = new Error(mode === "all" ? "当前页没有离线角色可启动" : "请先选择离线角色");
      toast.error(error.message);
      throw error;
    }

    let ok = 0;
    let failed = 0;
    let completed = 0;
    const results: RestartBatchResult[] = [];
    const initialState: StartBatchState = {
      running: true,
      mode,
      total: candidates.length,
      completed: 0,
      ok: 0,
      failed: 0,
      intervalSec,
      agentCount: 0,
      resolving: true,
      currentByAgent: {},
      waitingByAgent: {},
      results: [],
    };
    setRestartRun(null);
    setStartRun(initialState);

    const readyTargets: StartTarget[] = [];
    for (const candidate of candidates) {
      try {
        readyTargets.push(await resolveStartTarget(candidate));
      } catch (err) {
        failed += 1;
        completed += 1;
        results.push({
          instanceId: candidate.instanceId,
          label: candidate.label,
          ok: false,
          error: err instanceof Error ? err.message : String(err),
        });
        setStartRun((current) =>
          current
            ? {
                ...current,
                completed,
                failed,
                results: [...results],
              }
            : current,
        );
      }
    }

    const groups = startTargetGroups(readyTargets);
    setStartRun((current) =>
      current
        ? {
            ...current,
            resolving: false,
            agentCount: groups.length,
          }
        : current,
    );

    await Promise.all(
      groups.map(async (group) => {
        for (let index = 0; index < group.targets.length; index += 1) {
          const target = group.targets[index];
          const activeLabel = `${target.agentLabel} / ${target.label}`;
          setStartRun((current) =>
            current
              ? {
                  ...current,
                  currentByAgent: labelMapWith(current.currentByAgent, group.key, activeLabel),
                  waitingByAgent: labelMapWithout(current.waitingByAgent, group.key),
                }
              : current,
          );
          try {
            await apiPostForm<RoleLaunchBindingActionResponse>(
              roleLaunchBindingStartPath(target.bindingId),
              {},
            );
            ok += 1;
            results.push({
              instanceId: target.instanceId,
              label: target.label,
              ok: true,
            });
          } catch (err) {
            failed += 1;
            results.push({
              instanceId: target.instanceId,
              label: target.label,
              ok: false,
              error: err instanceof Error ? err.message : String(err),
            });
          }

          completed += 1;
          setStartRun((current) =>
            current
              ? {
                  ...current,
                  completed,
                  ok,
                  failed,
                  currentByAgent: labelMapWithout(current.currentByAgent, group.key),
                  results: [...results],
                }
              : current,
          );
          void queryClient.invalidateQueries({ queryKey });

          if (index < group.targets.length - 1) {
            const nextTarget = group.targets[index + 1];
            setStartRun((current) =>
              current
                ? {
                    ...current,
                    waitingByAgent: labelMapWith(
                      current.waitingByAgent,
                      group.key,
                      `${nextTarget.agentLabel} / ${nextTarget.label}`,
                    ),
                  }
                : current,
            );
            await delay(intervalSec * 1000);
          }
        }
      }),
    );

    setStartRun((current) =>
      current
        ? {
            ...current,
            running: false,
            resolving: false,
            completed: candidates.length,
            ok,
            failed,
            currentByAgent: {},
            waitingByAgent: {},
            results: [...results],
          }
        : current,
    );
    if (failed > 0) {
      toast.error(`批量启动完成：成功 ${ok}，失败 ${failed}`);
    } else {
      toast.success(`批量启动已派发：${ok} 个角色`);
    }
    void queryClient.invalidateQueries({ queryKey });
  }

  const pendingRestartTargets = confirmMode ? restartTargets(confirmMode) : [];
  const pendingRestartAgentCount =
    restartTargetGroups(pendingRestartTargets).length;
  const pendingStartTargets = startConfirmMode ? startCandidates(startConfirmMode) : [];

  return (
    <div className="space-y-4">
      <QueryView query={q}>
        {(data) => (
          <div className="space-y-3">
            <InstanceFilters
              search={search}
              options={filterOptions}
              filteredCount={data.total}
              page={currentPage}
              pageCount={pageCount}
              rangeStart={pageBounds.start}
              rangeEnd={pageBounds.end}
              onFilterChange={setFilter}
              onReset={resetFilters}
            />
            <BatchRestartToolbar
              canWrite={canWrite}
              onlineCount={onlineCount}
              pageOnlineCount={onlineRows.length}
              pageOfflineCount={offlineRows.length}
              selectedCount={selectedOnlineCount}
              selectedOfflineCount={selectedOfflineCount}
              intervalText={restartIntervalText}
              onIntervalChange={setRestartIntervalText}
              running={batchRunning}
              restartState={restartRun}
              startState={startRun}
              onRestartSelected={() => openRestartConfirm("selected")}
              onRestartAll={() => openRestartConfirm("all")}
              onStartSelected={() => openStartConfirm("selected")}
              onStartAll={() => openStartConfirm("all")}
            />
            <Table dense>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8">
                    <Checkbox
                      aria-label="选择本页角色"
                      checked={
                        allRowsSelected
                          ? true
                          : someRowsSelected
                            ? "indeterminate"
                            : false
                      }
                      disabled={!canWrite || batchRunning || rows.length === 0}
                      onCheckedChange={(checked) =>
                        setAllRowsSelected(checked === true)
                      }
                    />
                  </TableHead>
                  <TableHead>状态</TableHead>
                  <TableHead>游戏</TableHead>
                  <TableHead>服务器</TableHead>
                  <TableHead>角色</TableHead>
                  <TableHead>角色摘要</TableHead>
                  <TableHead>账号</TableHead>
                  <TableHead>当前地图</TableHead>
                  <TableHead>货币</TableHead>
                  <TableHead>材料</TableHead>
                  <TableHead>每小时收益</TableHead>
                  <TableHead>操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={12}
                      className="py-6 text-center text-[hsl(var(--muted-foreground))]"
                    >
                      当前筛选没有角色
                    </TableCell>
                  </TableRow>
                ) : null}
                {rows.map((i) => {
                  const checked = selectedIDs.has(i.instance_id);
                  const label = i.role_name || i.instance_id;
                  return (
                    <TableRow
                      key={i.role_key || i.instance_id}
                      data-state={checked ? "selected" : undefined}
                    >
                      <TableCell>
                        <Checkbox
                          aria-label={`选择角色 ${label}`}
                          checked={checked}
                          disabled={!canWrite || batchRunning}
                          onCheckedChange={(next) =>
                            setRowSelected(i.instance_id, next === true)
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <StatusBadge
                          variant={
                            !i.online
                              ? "neutral"
                              : i.state === "ready"
                                ? "ok"
                                : "warn"
                          }
                        >
                          {i.online ? (i.state ?? "在线") : "离线快照"}
                        </StatusBadge>
                      </TableCell>
                      <TableCell>{i.game_name ?? i.game_id ?? ""}</TableCell>
                      <TableCell>{i.server_name ?? ""}</TableCell>
                      <TableCell>
                        <RoleLink
                          instanceId={i.instance_id}
                          roleName={i.role_name}
                          roleId={i.role_id}
                          mergedCount={i.merged_count}
                        />
                      </TableCell>
                      <TableCell>
                        <RoleSummary
                          combatPower={i.combat_power}
                          combatPowerRank={i.combat_power_rank}
                          vipLevel={i.vip_level}
                          vipLabel={i.vip_label}
                          activeTitleName={i.active_title_name}
                        />
                      </TableCell>
                      <TableCell>
                        <AccountLink
                          accountId={i.account_id}
                          gameAccount={i.game_account}
                        />
                      </TableCell>
                      <TableCell>
                        <MapSummary
                          id={i.current_map_id}
                          name={i.current_map_name}
                        />
                      </TableCell>
                      <TableCell>
                        <MetricSummary items={i.currencies ?? []} />
                      </TableCell>
                      <TableCell>
                        <MetricSummary items={i.materials ?? []} />
                      </TableCell>
                      <TableCell>
                        <ProfitSummaryCell profit={i.profit} />
                      </TableCell>
                      <TableCell>
                        <InstanceActions
                          instanceId={i.instance_id}
                          roleName={i.role_name}
                          online={i.online}
                          pending={deleteMut.isPending || batchRunning}
                          onDelete={(instanceId) => {
                            if (!window.confirm(`删除离线角色快照 ${label}？`)) {
                              return;
                            }
                            deleteMut.mutate(instanceId);
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <InstancePager
              page={currentPage}
              pageCount={pageCount}
              total={totalCount}
              rangeStart={pageBounds.start}
              rangeEnd={pageBounds.end}
              onPageChange={goToPage}
            />
          </div>
        )}
      </QueryView>
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        resource="instances"
        action={confirmMode === "all" ? "restart_page" : "restart_selected"}
        target={`${pendingRestartTargets.length}-roles`}
        actor={actor}
        description={`重启当前页 ${pendingRestartTargets.length} 个在线角色，按 ${pendingRestartAgentCount} 个 agent 分组；同一 agent 内间隔 ${restartIntervalSec()} 秒。`}
        extra={<RestartConfirmSummary targets={pendingRestartTargets} />}
        onConfirm={() => {
          void runRestartBatch(confirmMode ?? "selected").catch(() => undefined);
        }}
      />
      <ConfirmDialog
        open={startConfirmOpen}
        onOpenChange={setStartConfirmOpen}
        resource="instances"
        action={startConfirmMode === "all" ? "start_page" : "start_selected"}
        target={`${pendingStartTargets.length}-offline-roles`}
        actor={actor}
        description={`启动当前页 ${pendingStartTargets.length} 个离线角色；执行时会先检查固定槽位启动上下文，同一 agent 内间隔 ${restartIntervalSec()} 秒。`}
        confirmVariant="destructive"
        extra={<StartConfirmSummary targets={pendingStartTargets} />}
        onConfirm={() => {
          void runStartBatch(startConfirmMode ?? "selected").catch(() => undefined);
        }}
      />
    </div>
  );
}

function InstanceFilters({
  search,
  options,
  filteredCount,
  page,
  pageCount,
  rangeStart,
  rangeEnd,
  onFilterChange,
  onReset,
}: {
  search: InstancesSearch;
  options: InstanceListFilterOptions;
  filteredCount: number;
  page: number;
  pageCount: number;
  rangeStart: number;
  rangeEnd: number;
  onFilterChange: (
    key: "server" | "game" | "account" | "agent",
    value: string,
  ) => void;
  onReset: () => void;
}) {
  return (
    <div className="rounded border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-3">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-5">
        <InstanceFilterSelect
          label="服务器（区服）"
          value={search.server}
          options={options.servers}
          onChange={(value) => onFilterChange("server", value)}
        />
        <InstanceFilterSelect
          label="游戏"
          value={search.game}
          options={options.games}
          onChange={(value) => onFilterChange("game", value)}
        />
        <InstanceFilterSelect
          label="账号"
          value={search.account}
          options={options.accounts}
          onChange={(value) => onFilterChange("account", value)}
        />
        <InstanceFilterSelect
          label="agent"
          value={search.agent}
          options={options.agents}
          onChange={(value) => onFilterChange("agent", value)}
        />
        <div className="flex items-end gap-2">
          <Button type="button" size="sm" variant="outline" onClick={onReset}>
            重置
          </Button>
        </div>
      </div>
      <div className="mt-2 text-xs text-[hsl(var(--muted-foreground))]">
        筛选结果 {filteredCount} 个角色；显示 {rangeStart}-{rangeEnd}，
        第 {page}/{pageCount} 页，每页 {INSTANCE_PAGE_SIZE} 条。
      </div>
    </div>
  );
}

function InstanceFilterSelect({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value?: string;
  options: InstanceListFilterOption[];
  onChange: (value: string) => void;
}) {
  return (
    <label className="block text-xs">
      <span className="mb-1 block text-[hsl(var(--muted-foreground))]">
        {label}
      </span>
      <Select value={value ?? ALL_FILTER_VALUE} onValueChange={onChange}>
        <SelectTrigger aria-label={label}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL_FILTER_VALUE}>全部</SelectItem>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </label>
  );
}

function InstancePager({
  page,
  pageCount,
  total,
  rangeStart,
  rangeEnd,
  onPageChange,
}: {
  page: number;
  pageCount: number;
  total: number;
  rangeStart: number;
  rangeEnd: number;
  onPageChange: (page: number) => void;
}) {
  return (
    <div className="flex flex-col gap-2 text-xs text-[hsl(var(--muted-foreground))] sm:flex-row sm:items-center sm:justify-between">
      <div>
        {rangeStart}-{rangeEnd} / {total} · 每页 {INSTANCE_PAGE_SIZE}
      </div>
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={page <= 1}
          onClick={() => onPageChange(1)}
        >
          首页
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={page <= 1}
          onClick={() => onPageChange(Math.max(1, page - 1))}
        >
          上一页
        </Button>
        <span className="flex h-8 items-center px-1 font-mono text-[hsl(var(--foreground))]">
          {page}/{pageCount}
        </span>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={page >= pageCount}
          onClick={() => onPageChange(Math.min(pageCount, page + 1))}
        >
          下一页
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={page >= pageCount}
          onClick={() => onPageChange(pageCount)}
        >
          末页
        </Button>
      </div>
    </div>
  );
}

function BatchRestartToolbar({
  canWrite,
  onlineCount,
  pageOnlineCount,
  pageOfflineCount,
  selectedCount,
  selectedOfflineCount,
  intervalText,
  onIntervalChange,
  running,
  restartState,
  startState,
  onRestartSelected,
  onRestartAll,
  onStartSelected,
  onStartAll,
}: {
  canWrite: boolean;
  onlineCount: number;
  pageOnlineCount: number;
  pageOfflineCount: number;
  selectedCount: number;
  selectedOfflineCount: number;
  intervalText: string;
  onIntervalChange: (value: string) => void;
  running: boolean;
  restartState: RestartBatchState | null;
  startState: StartBatchState | null;
  onRestartSelected: () => void;
  onRestartAll: () => void;
  onStartSelected: () => void;
  onStartAll: () => void;
}) {
  return (
    <div className="rounded border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-3">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div className="grid grid-cols-2 gap-3 text-xs sm:grid-cols-3 xl:grid-cols-6">
          <SummaryMetric label="筛选在线" value={onlineCount} />
          <SummaryMetric label="本页在线" value={pageOnlineCount} />
          <SummaryMetric label="本页离线" value={pageOfflineCount} />
          <SummaryMetric label="已选在线" value={selectedCount} />
          <SummaryMetric label="已选离线" value={selectedOfflineCount} />
          <label className="block min-w-24">
            <span className="mb-1 block text-[hsl(var(--muted-foreground))]">
              间隔秒
            </span>
            <Input
              aria-label="重启间隔秒"
              type="number"
              min={1}
              max={600}
              inputMode="numeric"
              value={intervalText}
              disabled={running}
              onChange={(event) => onIntervalChange(event.target.value)}
              className="h-8 w-24"
            />
          </label>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={!canWrite || running || selectedCount === 0}
            onClick={onRestartSelected}
          >
            <RefreshCw className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
            重启已选
          </Button>
          <Button
            type="button"
            size="sm"
            variant="destructive"
            disabled={!canWrite || running || pageOnlineCount === 0}
            onClick={onRestartAll}
          >
            <RefreshCw className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
            重启本页
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={!canWrite || running || selectedOfflineCount === 0}
            onClick={onStartSelected}
          >
            <Play className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
            启动已选
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={!canWrite || running || pageOfflineCount === 0}
            onClick={onStartAll}
          >
            <Play className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
            启动本页
          </Button>
        </div>
      </div>
      {restartState ? <RestartBatchProgress state={restartState} /> : null}
      {startState ? <StartBatchProgress state={startState} /> : null}
    </div>
  );
}

function SummaryMetric({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <div className="text-[hsl(var(--muted-foreground))]">{label}</div>
      <div className="font-mono text-sm font-medium">{value}</div>
    </div>
  );
}

function RestartBatchProgress({ state }: { state: RestartBatchState }) {
  const latestFailure = [...state.results].reverse().find((item) => !item.ok);
  const activeLabels = Object.values(state.currentByAgent);
  const waitingLabels = Object.values(state.waitingByAgent);
  const status = state.running
    ? activeLabels.length > 0
      ? `正在重启 ${activeLabels.length} 个 agent 队列：${activeLabels[0]}`
      : waitingLabels.length > 0
        ? `等待 ${state.intervalSec}s 后继续 ${waitingLabels.length} 个 agent 队列：${waitingLabels[0]}`
        : "等待下一项"
    : "已完成";
  return (
    <div className="mt-3 rounded border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30 p-2 text-xs">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        <span className="font-medium">{status}</span>
        <span className="font-mono">
          {state.completed}/{state.total}
        </span>
        <span>agent {state.agentCount}</span>
        <span>成功 {state.ok}</span>
        <span>失败 {state.failed}</span>
      </div>
      {latestFailure ? (
        <div className="mt-1 text-[hsl(var(--destructive))]">
          {latestFailure.label}：{latestFailure.error ?? "未知错误"}
        </div>
      ) : null}
    </div>
  );
}

function StartBatchProgress({ state }: { state: StartBatchState }) {
  const latestFailure = [...state.results].reverse().find((item) => !item.ok);
  const activeLabels = Object.values(state.currentByAgent);
  const waitingLabels = Object.values(state.waitingByAgent);
  const status = state.resolving
    ? "正在检查固定槽位启动上下文"
    : state.running
      ? activeLabels.length > 0
        ? `正在启动 ${activeLabels.length} 个 agent 队列：${activeLabels[0]}`
        : waitingLabels.length > 0
          ? `等待 ${state.intervalSec}s 后继续 ${waitingLabels.length} 个 agent 队列：${waitingLabels[0]}`
          : "等待下一项"
      : "已完成";
  return (
    <div className="mt-3 rounded border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30 p-2 text-xs">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        <span className="font-medium">{status}</span>
        <span className="font-mono">
          {state.completed}/{state.total}
        </span>
        <span>agent {state.agentCount}</span>
        <span>成功 {state.ok}</span>
        <span>失败 {state.failed}</span>
      </div>
      {latestFailure ? (
        <div className="mt-1 text-[hsl(var(--destructive))]">
          {latestFailure.label}：{latestFailure.error ?? "未知错误"}
        </div>
      ) : null}
    </div>
  );
}

function RestartConfirmSummary({ targets }: { targets: RestartTarget[] }) {
  const visible = targets.slice(0, 8);
  const groups = restartTargetGroups(targets);
  return (
    <div className="rounded border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30 p-2 text-xs">
      <div className="mb-1 text-[hsl(var(--muted-foreground))]">
        当前页目标角色 {targets.length} · agent 队列 {groups.length}
      </div>
      {groups.length > 0 ? (
        <div className="mb-2 flex flex-wrap gap-1">
          {groups.map((group) => (
            <span
              key={group.key}
              className="rounded border border-[hsl(var(--border))] px-1.5 py-0.5 text-[hsl(var(--muted-foreground))]"
            >
              {group.label} × {group.targets.length}
            </span>
          ))}
        </div>
      ) : null}
      <ul className="max-h-40 space-y-1 overflow-auto">
        {visible.map((target) => (
          <li key={target.instanceId} className="flex min-w-0 gap-2">
            <span className="font-medium">{target.label}</span>
            {target.server ? (
              <span className="truncate text-[hsl(var(--muted-foreground))]">
                {target.game ? `${target.game} · ` : ""}
                {target.server}
              </span>
            ) : null}
          </li>
        ))}
        {targets.length > visible.length ? (
          <li className="text-[hsl(var(--muted-foreground))]">
            另有 {targets.length - visible.length} 个角色
          </li>
        ) : null}
      </ul>
    </div>
  );
}

function StartConfirmSummary({ targets }: { targets: StartCandidate[] }) {
  const visible = targets.slice(0, 8);
  return (
    <div className="rounded border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30 p-2 text-xs">
      <div className="mb-1 text-[hsl(var(--muted-foreground))]">
        当前页离线目标 {targets.length}
      </div>
      <ul className="max-h-40 space-y-1 overflow-auto">
        {visible.map((target) => (
          <li key={target.instanceId} className="flex min-w-0 gap-2">
            <span className="font-medium">{target.label}</span>
            {target.server ? (
              <span className="truncate text-[hsl(var(--muted-foreground))]">
                {target.game ? `${target.game} · ` : ""}
                {target.server}
              </span>
            ) : null}
          </li>
        ))}
        {targets.length > visible.length ? (
          <li className="text-[hsl(var(--muted-foreground))]">
            另有 {targets.length - visible.length} 个角色
          </li>
        ) : null}
      </ul>
    </div>
  );
}

function InstanceActions({
  instanceId,
  roleName,
  online,
  pending,
  onDelete,
}: {
  instanceId: string;
  roleName?: string;
  online: boolean;
  pending: boolean;
  onDelete: (instanceId: string) => void;
}) {
  if (online) {
    return <span className="text-xs text-[hsl(var(--muted-foreground))]">—</span>;
  }
  return (
    <Button
      type="button"
      size="sm"
      variant="destructive"
      disabled={pending}
      title="删除离线角色快照"
      aria-label={`删除离线角色快照 ${roleName || instanceId}`}
      onClick={() => onDelete(instanceId)}
    >
      <Trash2 className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
      删除
    </Button>
  );
}

function RoleLink({
  instanceId,
  roleName,
  roleId,
  mergedCount,
}: {
  instanceId: string;
  roleName?: string;
  roleId?: string;
  mergedCount?: number;
}) {
  return (
    <div className="flex min-w-36 flex-col gap-1">
      <Link
        to="/instances/$instanceId"
        params={{ instanceId }}
        className="hover:underline"
      >
        {roleName || "未命名角色"}
      </Link>
      <div className="flex flex-wrap items-center gap-1 text-[10px] text-[hsl(var(--muted-foreground))]">
        {roleId ? <code>#{roleId}</code> : null}
        {mergedCount && mergedCount > 1 ? (
          <span>已合并 {mergedCount} 个连接快照</span>
        ) : null}
      </div>
    </div>
  );
}

function RoleSummary({
  combatPower,
  combatPowerRank,
  vipLevel,
  vipLabel,
  activeTitleName,
}: {
  combatPower?: number;
  combatPowerRank?: number;
  vipLevel?: string;
  vipLabel?: string;
  activeTitleName?: string;
}) {
  const items: string[] = [];
  if (typeof combatPower === "number" && combatPower > 0) {
    const rank = formatCombatPowerRank(combatPowerRank);
    items.push(`战力 ${formatCombatPower(combatPower)}${rank ? `（${rank}）` : ""}`);
  }
  const titleVip = parseVipTitleName(activeTitleName);
  if (vipLabel) {
    items.push(vipLabel);
  } else if (vipLevel) {
    items.push(`VIP ${vipLevel}`);
  } else if (titleVip) {
    items.push(titleVip.label);
  }
  if (items.length === 0) {
    return <span className="text-[hsl(var(--muted-foreground))]">—</span>;
  }
  return <span className="whitespace-nowrap">{items.join(" · ")}</span>;
}

function parseVipTitleName(name?: string) {
  const text = name?.trim() ?? "";
  if (!text) return undefined;
  if (text.includes("顶级VIP")) {
    return { level: 11, label: "顶级VIP" };
  }
  const levelMatch =
    text.match(/([1-9]\d?)\s*级\s*VIP/i) ?? text.match(/VIP\s*([1-9]\d?)/i);
  const level = levelMatch ? Number(levelMatch[1]) : 0;
  if (level >= 1 && level <= 10) {
    return { level, label: `${level}级VIP` };
  }
  return undefined;
}

function AccountLink({
  accountId,
  gameAccount,
}: {
  accountId?: number;
  gameAccount?: string;
}) {
  if (!gameAccount) {
    return <span className="text-[hsl(var(--muted-foreground))]">—</span>;
  }
  if (!accountId) {
    return <span>{gameAccount}</span>;
  }
  return (
    <Link
      to="/accounts/$accountId"
      params={{ accountId: String(accountId) }}
      className="hover:underline"
    >
      {gameAccount}
    </Link>
  );
}

function MapSummary({ id, name }: { id?: string; name?: string }) {
  if (!id && !name) {
    return <span className="text-[hsl(var(--muted-foreground))]">—</span>;
  }
  return (
    <span className="whitespace-nowrap">
      {name || "—"}
      {id ? (
        <code className="ml-1 text-[hsl(var(--muted-foreground))]">#{id}</code>
      ) : null}
    </span>
  );
}

function MetricSummary({
  items,
}: {
  items: NonNullable<InstanceListResponse["items"][number]["currencies"]>;
}) {
  if (!items || items.length === 0) {
    return <span className="text-[hsl(var(--muted-foreground))]">—</span>;
  }
  return (
    <div className="flex min-w-32 flex-col gap-1">
      {items.map((item, index) => (
        <span
          key={`${item.id || item.label || "?"}-${index}`}
          className="whitespace-nowrap text-xs"
          title={item.id ? `id=${item.id}` : undefined}
        >
          <span className="text-[hsl(var(--muted-foreground))]">
            {item.label || (item.id ? `#${item.id}` : "未命名")}
          </span>{" "}
          <strong className="font-mono">{compactNumber(item.count)}</strong>
        </span>
      ))}
    </div>
  );
}

function ProfitSummaryCell({
  profit,
}: {
  profit?: InstanceListResponse["items"][number]["profit"];
}) {
  if (!profit || (profit.currencies.length === 0 && profit.materials.length === 0)) {
    return (
      <span className="whitespace-nowrap text-xs text-[hsl(var(--muted-foreground))]">
        暂无收益事件
      </span>
    );
  }
  const currencies = fixedProfitCurrencies(profit);
  const netValueText =
    typeof profit.net_value_per_hour === "number"
      ? `${formatHourlyAmount(profit.net_value_per_hour)}/h`
      : "";
  const sampleText =
    profit.sample_status !== "ok"
      ? `样本 ${Math.max(0, Math.floor((profit.active_seconds ?? 0) / 60))}m`
      : "";
  const title = [
    "收益事件",
    `净值 ${netValueText || "未估值"}`,
    ...currencies.map(
      (item) =>
        `${item.name || `#${item.id}`} 产出 ${formatHourlyAmount(item.gain_per_hour ?? 0)}/h`,
    ),
    sampleText,
  ]
    .filter(Boolean)
    .join("；");
  return (
    <div className="flex min-w-44 flex-col gap-1 text-xs" title={title}>
      <span className="whitespace-nowrap">
        <span className="text-[hsl(var(--muted-foreground))]">净值</span>{" "}
        {netValueText ? (
          <strong className="font-mono">{netValueText}</strong>
        ) : (
          <span className="text-[hsl(var(--muted-foreground))]">未估值</span>
        )}
      </span>
      {currencies.map((item) => (
        <span key={item.id} className="whitespace-nowrap">
          <span className="text-[hsl(var(--muted-foreground))]">
            {item.name || `#${item.id}`}
          </span>{" "}
          <strong className="font-mono">
            {formatHourlyAmount(item.gain_per_hour ?? 0)}/h
          </strong>
        </span>
      ))}
      {sampleText ? (
        <span className="whitespace-nowrap text-[hsl(var(--muted-foreground))]">
          {sampleText}
        </span>
      ) : null}
    </div>
  );
}

function fixedProfitCurrencies(
  profit: NonNullable<InstanceListResponse["items"][number]["profit"]>,
) {
  const byID = new Map(profit.currencies.map((item) => [String(item.id), item]));
  return PROFIT_CURRENCY_ORDER.map((meta) => {
    const item = byID.get(meta.id);
    if (item) {
      return { ...item, id: meta.id, name: meta.name };
    }
    return {
      id: meta.id,
      name: meta.name,
      gain: 0,
      spend: 0,
      net: 0,
      gain_per_hour: 0,
    };
  });
}

function formatHourlyAmount(value: number): string {
  if (!Number.isFinite(value)) return "0";
  return compactNumber(value);
}

function delay(ms: number) {
  return new Promise<void>((resolve) => window.setTimeout(resolve, ms));
}
